import { useState } from 'react'
import { useTexte } from '../../texte'

import { useSaveSettings, useSettings } from '../../hooks/queries'
import { fehlertext } from '../../api/client'

/**
 * Ort und Wetter: ist es eingerichtet?
 *
 * Beim Einrichten fehlte genau diese Auskunft. Der Server weiß es und sagt es
 * auch (`/api/settings` → `services`), aber zu sehen war es nirgends — der
 * Weg war, die Adresse von Hand im Browser aufzurufen und im JSON
 * nachzusehen. Genau die Art Umweg, die diese Anwendung sonst vermeidet:
 * ausgeliefert wird per FTP, die `.env` bearbeitet ein Mensch mit einem
 * Texteditor, und ob ein Tippfehler darin steht, soll die Anwendung sagen.
 *
 * **Die Karte zeigt sich nur der Verwaltung** — und zwar daran erkennbar,
 * dass der Server die Decke mitschickt, nicht weil die Oberfläche aus der
 * Rolle schließt. Dasselbe Muster wie in `LimitsCard`.
 *
 * **„Nicht eingerichtet" ist keine Fehlermeldung.** Ohne die Dienste läuft
 * alles weiter; ein Vorschlag aus der Route heißt dann eben „Aufenthalt,
 * 2 Std." statt „Trondheim". Das steht auch so da — sonst sucht jemand einen
 * Fehler, den es nicht gibt.
 */
export function DiensteCard() {
  const t = useTexte()
  const settings = useSettings()
  const save = useSaveSettings()
  const [entwurf, setEntwurf] = useState<string | null>(null)
  const [weitEntwurf, setWeitEntwurf] = useState<string | null>(null)
  const [gemeldet, setGemeldet] = useState(false)
  const daten = settings.data

  // Ohne Decke ist dieses Konto keine Verwaltung -- der Server hat entschieden.
  if (!daten?.ceiling) return null

  const dienste = daten.services ?? { place: false, weather: false }
  const grenzen = daten.limits
  const umkreis = entwurf ?? String(daten.settings.poi_radius_m)
  const weitUmkreis = weitEntwurf ?? String(daten.settings.poi_extra_radius_m)
  const weitGeaendert =
    Number.isInteger(Number(weitUmkreis)) &&
    Number(weitUmkreis) !== daten.settings.poi_extra_radius_m
  const geaendert =
    Number.isInteger(Number(umkreis)) && Number(umkreis) !== daten.settings.poi_radius_m
  const beides = dienste.place && dienste.weather
  const nichts = !dienste.place && !dienste.weather

  /**
   * **Der Fall, den man sonst nirgends sieht.** Alle Angaben stehen in der
   * `.env`, und trotzdem läuft nichts — weil unter dem eingetragenen Pfad
   * keine lesbare Datei liegt. Ein Tippfehler, ein falsches Verzeichnis,
   * vergessene Leserechte; getippt wird der Pfad von Hand, per FTP, unterwegs.
   *
   * Vorher sagte die Statuszeile in genau diesem Fall „eingerichtet", die
   * Knöpfe erschienen, und jeder Abruf ging still ins Leere.
   */
  const detail = daten.services_detail
  const schluesselFehlt =
    detail !== undefined &&
    !detail.key_readable &&
    (detail.place_configured || detail.weather_configured)

  return (
    <div className="card card--seite" data-dienste="">
      <h2 className="card__titel">{t.dienste.titel}</h2>
      <p className="card__lead card__lead--absatz">{t.dienste.lead}</p>

      <p className="dienst">
        <span className="dienst__name">{t.dienste.ortsnamen}</span>
        <Stand an={dienste.place} />
      </p>
      <p className="dienst">
        <span className="dienst__name">{t.dienste.wetter}</span>
        <Stand an={dienste.weather} />
      </p>
      {/* Die Emoji-Bilder fürs Buch (0.36.1): ohne die Datei fehlen im PDF
          alle Emojis, und nichts sagte es. Aus dem Betrieb: „Emojis fehlen". */}
      {detail?.book_emoji !== undefined && (
        <p className="dienst" data-dienst="buch-emoji">
          <span className="dienst__name">{t.bausteine.buchEmoji}</span>
          <Stand an={detail.book_emoji} />
        </p>
      )}
      {detail?.book_emoji === false && (
        <p className="notice" role="alert">{t.dienste.buchEmojiFehlt}</p>
      )}

      {schluesselFehlt ? (
        <p className="dienst__fehler" role="alert">{t.dienste.schluesselFehlt}</p>
      ) : nichts ? (
        <p className="card__lead card__lead--zeile">{t.dienste.aus}</p>
      ) : !beides ? (
        <p className="card__lead card__lead--zeile">{t.dienste.halb}</p>
      ) : (
        <p className="card__lead card__lead--zeile">{t.dienste.an}</p>
      )}

      {!beides && !nichts && !schluesselFehlt && (
        <ul className="dienst__zeilen">
          <li>APPLE_TEAM_ID</li>
          <li>APPLE_MAPS_KEY_ID</li>
          <li>APPLE_WEATHER_KEY_ID</li>
          <li>APPLE_WEATHER_SERVICE_ID</li>
          <li>APPLE_PRIVATE_KEY_PATH</li>
        </ul>
      )}

      {/* **Nur wo die Ortssuche läuft.** Ohne sie gibt es den Knopf „In der
          Nähe finden" nicht, und eine Zahl für etwas Abgeschaltetes
          einzustellen wäre ein Rätsel statt einer Einstellung. */}
      {dienste.place && grenzen !== undefined && (
        <label className="field field--zwischen">
          <span className="field__label">{t.dienste.umkreis}</span>
          <input
            className="field__input"
            type="number"
            inputMode="numeric"
            name="poi_radius_m"
            value={umkreis}
            min={grenzen.poi_radius_m.min}
            max={grenzen.poi_radius_m.max}
            onChange={(e) => {
              setGemeldet(false)
              setEntwurf(e.target.value)
            }}
          />
          <span className="feld__hinweis">{t.dienste.umkreisHinweis}</span>
          <span className="angabe">
            <button
              className="button button--outline button--small"
              type="button"
              disabled={!geaendert || save.isPending}
              onClick={() =>
                save.mutate(
                  { poi_radius_m: Number(umkreis) },
                  {
                    onSuccess: () => {
                      setEntwurf(null)
                      setGemeldet(true)
                    },
                  },
                )
              }
            >
              {save.isPending ? t.allgemein.laeuft : t.allgemein.speichern}
            </button>
            {gemeldet && !save.isPending && (
              <span className="winzig akzent">{t.allgemein.gespeichert}</span>
            )}
            {save.isError && (
              <span className="winzig" role="alert">
                {fehlertext(save.error)}
              </span>
            )}
          </span>

          {/* **Der zweite Umkreis, und er beantwortet eine andere Frage.**
              Der obere: „in *welchem* Lokal war ich?" — da zählt der Meter.
              Dieser: „was war an dem Tag noch da?" — da ist ein Museum drei
              Straßen weiter genau richtig. */}
          <span className="field__label field__label--oben">
            {t.dienste.weitUmkreis}
          </span>
          <input
            className="field__input"
            type="number"
            inputMode="numeric"
            name="poi_extra_radius_m"
            value={weitUmkreis}
            min={grenzen.poi_extra_radius_m?.min ?? 100}
            max={grenzen.poi_extra_radius_m?.max}
            onChange={(e) => {
              setGemeldet(false)
              setWeitEntwurf(e.target.value)
            }}
          />
          <span className="feld__hinweis">
            {t.dienste.weitUmkreisHinweis}
            {Number(weitUmkreis) < Number(umkreis) && (
              <strong> {t.dienste.weitZuKlein}</strong>
            )}
          </span>
          <span className="angabe">
            <button
              className="button button--outline button--small"
              type="button"
              disabled={!weitGeaendert || save.isPending}
              onClick={() =>
                save.mutate(
                  { poi_extra_radius_m: Number(weitUmkreis) },
                  {
                    onSuccess: () => {
                      setWeitEntwurf(null)
                      setGemeldet(true)
                    },
                  },
                )
              }
            >
              {save.isPending ? t.allgemein.laeuft : t.allgemein.speichern}
            </button>
          </span>
        </label>
      )}

      {!beides && (
        // Kein Neustart nötig -- hier läuft kein Prozess durch, der Server
        // liest die Datei bei jeder Anfrage neu. Die Oberfläche hält den Wert
        // allerdings zehn Minuten im Zwischenspeicher.
        <p className="dienst__hinweis">
          {t.dienste.envVor}
          <code>.env</code>
          {t.dienste.envNach}
        </p>
      )}
    </div>
  )
}

/**
 * Die Statuszeile eines Dienstes -- geteilt mit `GifCard`.
 *
 * „Nicht eingerichtet" ist kein Fehler und sieht deshalb nicht aus wie einer.
 */
export function Stand({ an }: { an: boolean }) {
  const t = useTexte()
  return (
    <span className={an ? 'dienst__stand dienst__stand--an' : 'dienst__stand'}>
      {an ? t.dienste.eingerichtet : t.dienste.nichtEingerichtet}
    </span>
  )
}
